import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { ThreeDot } from "react-loading-indicators";
import BASE_URL from "../../../assets/global/url";
import NoAccess from "../../../assets/img/NoAccess.png";
import { customStyles } from "../../../assets/global/table-style";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
import { NoTranslate } from "../../../helper/prevent_translate_browser";
import { formatToTwoDecimal } from "../../../utils/numberFormatter";
import AccountSummaryExportModal from "../../Accounts/components/ViewAccountList/AccountSummaryExportModal";
import "../../../assets/css/style.css";

// Subject 2 module_type values (see SubjectAccounts/subjectAccountTypes.js)
const ACCOUNT_TYPES = [
  { value: "", label: "All", icon: "fa-layer-group" },
  { value: "Account-List", label: "Account List", icon: "fa-building-columns" },
  { value: "Asset Account", label: "Asset", icon: "fa-boxes-stacked" },
  {
    value: "Liabilities Account",
    label: "Liability",
    icon: "fa-file-invoice-dollar",
  },
  {
    value: "Owner's Equity Account",
    label: "Owners' Equity",
    icon: "fa-handshake",
  },
];

// Subject 2 subject_type values
const SUBJECT_TYPES = [
  { value: "", label: "All" },
  { value: "Bank", label: "Bank", icon: "fa-building-columns" },
  { value: "Cash", label: "Cash", icon: "fa-money-bill-wave" },
];

const typeLabel = (moduleType) =>
  ACCOUNT_TYPES.find((type) => type.value === moduleType)?.label || "";

// "$", "₱", ... for the round badge in front of the account name
const currencySymbol = (code) => {
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((part) => part.type === "currency").value;
  } catch {
    return code?.charAt(0) || "";
  }
};

// Light header instead of the app-wide purple one (same as SubjectAccounts)
const tableStyles = {
  ...customStyles,
  headCells: {
    style: {
      ...customStyles.headCells.style,
      backgroundColor: "var(--sa-surface-2)",
      color: "var(--sa-muted)",
      fontWeight: "600",
      fontSize: "14px",
      padding: "14px 8px",
    },
  },
  rows: {
    style: {
      ...customStyles.rows.style,
      padding: "14px 0px",
    },
  },
  progress: {
    style: {
      ...customStyles.progress?.style,
      backgroundColor: "var(--sa-surface)",
      color: "var(--sa-muted)",
    },
  },
  noData: {
    style: {
      ...customStyles.noData?.style,
      backgroundColor: "var(--sa-surface)",
      color: "var(--sa-muted)",
    },
  },
};

// Standard Reports > Account Reports: every account_list_sub3 account.
// Clicking one opens the same export preview as View Account's Export
// (download needs the Account Report Import/Export permission).
const AccountReports = ({ authrztn = [] }) => {
  const [searchText, setSearchText] = useState("");
  const [moduleType, setModuleType] = useState("");
  const [subjectType, setSubjectType] = useState("");
  // { account, type: "pdf" | "excel" } while the export preview is open
  const [selected, setSelected] = useState(null);

  // No subjectId: getSubject3ForFilter returns accounts from every Subject 2
  const pagination = useServerPagination(
    BASE_URL + "/accountListSub/getSubject3ForFilter",
    10,
    // searchAll: search also matches type, currency and last balance
    { searchText: "", moduleType: "", subjectType: "", searchAll: true },
  );

  // Debounced search; the type filters apply right away
  useEffect(() => {
    const timer = setTimeout(() => {
      pagination.updateParams({ searchText, moduleType, subjectType });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    pagination.updateParams({ searchText, moduleType, subjectType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleType, subjectType]);

  const openExport = (account, type) => setSelected({ account, type });

  const columns = [
    {
      name: "Account",
      selector: (row) => (
        <NoTranslate>
          <span className="sa-account">
            <span className="sa-account-icon">
              {currencySymbol(row.currency?.currency_name).trim()}
            </span>
            <span className="d-flex flex-column">
              <span className="sa-account-name">{row.account_name}</span>
              <span className="sa-subtitle">
                {row.account_list_base_sub?.subject_name}
              </span>
            </span>
          </span>
        </NoTranslate>
      ),
      grow: 2,
      style: { justifyContent: "flex-start", paddingLeft: "24px" },
    },
    {
      name: "Type",
      selector: (row) => (
        <span className="d-flex flex-wrap gap-1">
          <span className="sa-type-badge">
            {typeLabel(row.account_list_base_sub?.module_type)}
          </span>
          {row.account_list_base_sub?.subject_type && (
            <span className="sa-type-badge">
              {row.account_list_base_sub.subject_type}
            </span>
          )}
        </span>
      ),
    },
    {
      name: "Currency",
      selector: (row) => row.currency?.currency_name || "",
    },
    {
      name: "Last Balance",
      selector: (row) => (
        <NoTranslate>
          <span
            className={`sa-amount fw-semibold ${
              Number(row.amount) < 0 ? "text-danger" : ""
            }`}
          >
            {formatToTwoDecimal(row.amount)}
          </span>
        </NoTranslate>
      ),
    },
    {
      name: "Preview",
      selector: (row) => (
        <div className="sa-row-actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="sa-tile-btn"
            title="PDF preview"
            onClick={() => openExport(row, "pdf")}
          >
            <i className="fa-solid fa-file-pdf text-danger"></i>
          </button>
          <button
            type="button"
            className="sa-tile-btn"
            title="Excel preview"
            onClick={() => openExport(row, "excel")}
          >
            <i className="fa-solid fa-file-excel text-success"></i>
          </button>
        </div>
      ),
    },
  ];

  if (!authrztn.includes("AccountReport-View")) {
    return (
      <div className="h-100 w-100 border bg-white custom-container">
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="h-100 w-100 border custom-container sa-page">
      {/* Header */}
      <div className="sa-header">
        <div className="d-flex align-items-center gap-3">
          <div className="sa-header-icon">
            <i className="fa-solid fa-file-lines"></i>
          </div>
          <div className="title-custom">
            <h1 className="sa-title">Account Reports</h1>
            <p className="sa-subtitle">
              Pick an account to preview its summary report (PDF / Excel)
            </p>
          </div>
        </div>
      </div>

      <div className="sa-card">
        {/* Type filters: account type + Bank / Cash */}
        <div className="ar-filters">
          <div className="ar-filter-group">
            <span className="ar-filter-label">Account Type</span>
            <div className="ar-type-tabs">
              {ACCOUNT_TYPES.map((type) => (
                <button
                  key={type.label}
                  type="button"
                  className={`ar-type-tab ${
                    moduleType === type.value ? "active" : ""
                  }`}
                  onClick={() => setModuleType(type.value)}
                >
                  <i className={`fa-solid ${type.icon}`}></i>
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ar-filter-group">
            <span className="ar-filter-label">Cash / Bank</span>
            <div className="ar-type-tabs">
              {SUBJECT_TYPES.map((type) => (
                <button
                  key={type.label}
                  type="button"
                  className={`ar-type-tab ${
                    subjectType === type.value ? "active" : ""
                  }`}
                  onClick={() => setSubjectType(type.value)}
                >
                  {type.icon && <i className={`fa-solid ${type.icon}`}></i>}
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="sa-search">
          <i className="fa-solid fa-magnifying-glass"></i>
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button
              type="button"
              className="sa-search-clear"
              title="Clear search"
              onClick={() => setSearchText("")}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
        </div>

        <DataTable
          columns={columns}
          data={pagination.data}
          customStyles={tableStyles}
          className="dataTable"
          progressPending={pagination.loading}
          progressComponent={
            <div className="sa-table-state">
              <ThreeDot
                variant="brick-stack"
                color="#6290FE"
                size="small"
                text="Fetching records..."
                textColor="#6c757d"
              />
            </div>
          }
          noDataComponent={
            <div className="sa-table-state">
              <i className="fa-regular fa-folder-open"></i>
              <span>
                {searchText
                  ? `No accounts match "${searchText}"`
                  : "No accounts found"}
              </span>
            </div>
          }
          onRowClicked={(row) => openExport(row, "pdf")}
          pointerOnHover
          highlightOnHover
        />
        <div className="sa-pagination">
          <PaginationControls {...pagination} />
        </div>
      </div>

      <AccountSummaryExportModal
        show={selected !== null}
        type={selected?.type}
        onHide={() => setSelected(null)}
        accountId={selected?.account.id}
        accountName={selected?.account.account_name}
        canDownload={authrztn.includes("AccountReport-IE")}
      />
    </div>
  );
};

export default AccountReports;
